import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { TableConnector, SyncMode, NangoSyncStatus } from '@loader/shared-types';

import { queryKeys } from '@/lib/queryKeys';
import { throwApiError, useRpc } from '@/lib/rpc';

// ============================================================================
// Shared Helpers
// ============================================================================

const syncRefetchInterval = (query: { state: { data: NangoSyncStatus[] | undefined } }) => {
  const hasRunning = query.state.data?.some((s) => s.status === 'RUNNING');
  return hasRunning ? 5_000 : 30_000;
};

// ============================================================================
// Types
// ============================================================================

export interface NangoIntegration {
  uniqueKey: string;
  displayName: string;
  provider: string;
  logo: string;
}

/**
 * Workspace Nango connections (from Nango API, tagged by workspace).
 * Includes `linked` flag to identify orphan connections without matching connectors.
 */
export interface WorkspaceNangoConnection {
  connectionId: string;
  providerConfigKey: string;
  created: string;
  linked: boolean;
}

// ============================================================================
// Integration Discovery
// ============================================================================

/**
 * Fetch all configured Nango integrations (for the integration picker).
 */
export function useNangoIntegrations() {
  const rpc = useRpc();

  return useQuery({
    queryKey: queryKeys.connectors.nangoIntegrations,
    queryFn: async (): Promise<NangoIntegration[]> => {
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.connectors.nango.integrations.$get();
      if (!res.ok) await throwApiError(res, 'Failed to fetch integrations');
      const json = (await res.json()) as { integrations: NangoIntegration[] };
      return json.integrations;
    },
    staleTime: 5 * 60_000, // 5 min -- list rarely changes
  });
}

// ============================================================================
// Session / Connection
// ============================================================================

/**
 * Get a Nango Connect session token for the OAuth flow.
 */
export function useNangoSessionToken() {
  const rpc = useRpc();

  return useMutation({
    mutationFn: async (integrations: string[] | undefined) => {
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.connectors.nango['session-token'].$post({
        json: { integrations },
      });
      if (!res.ok) await throwApiError(res, 'Failed to get Nango session token');
      const json = (await res.json()) as { sessionToken: string };
      return json.sessionToken;
    },
  });
}

/**
 * Save a Nango connection to a table connector after OAuth flow completes.
 */
export function useSaveNangoConnection() {
  const rpc = useRpc();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      tableId,
      connectionId,
      providerConfigKey,
      model,
    }: {
      tableId: string;
      connectionId: string;
      providerConfigKey: string;
      model: string;
    }) => {
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.connectors.nango[':tableId']['save-connection'].$post({
        param: { tableId },
        json: { connectionId, providerConfigKey, model },
      });
      if (!res.ok) await throwApiError(res, 'Failed to save Nango connection');
      const json = (await res.json()) as { connector: TableConnector };
      return json.connector;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.connectors.all });
    },
  });
}

/**
 * Update sync settings on an existing Nango connector.
 */
export function useUpdateNangoSettings() {
  const rpc = useRpc();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      tableId,
      syncMode,
      autoTagRotation,
    }: {
      tableId: string;
      syncMode?: SyncMode;
      autoTagRotation?: boolean;
    }) => {
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.connectors.nango[':tableId'].settings.$patch({
        param: { tableId },
        json: { syncMode, autoTagRotation },
      });
      if (!res.ok) await throwApiError(res, 'Failed to update settings');
      const json = (await res.json()) as { connector: TableConnector };
      return json.connector;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.connectors.all });
    },
  });
}

// ============================================================================
// Sync Triggers & Status
// ============================================================================

/**
 * Trigger an immediate Nango sync for a table's connector.
 * Invalidates sync-status after trigger so the UI refreshes.
 */
export function useTriggerNangoSync() {
  const rpc = useRpc();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (tableId: string) => {
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.connectors.nango[':tableId']['trigger-sync'].$post({
        param: { tableId },
      });
      if (!res.ok) await throwApiError(res, 'Failed to trigger sync');
      return tableId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.connectors.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.tables.all });
    },
  });
}

/**
 * Get the Nango sync status for a table's connector.
 */
export function useNangoSyncStatus(tableId: string | undefined) {
  const rpc = useRpc();

  return useQuery({
    queryKey: queryKeys.connectors.syncStatus(tableId ?? ''),
    queryFn: async (): Promise<NangoSyncStatus[]> => {
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.connectors.nango[':tableId']['sync-status'].$get({
        param: { tableId: tableId! },
      });
      if (!res.ok) await throwApiError(res, 'Failed to fetch sync status');
      const json = (await res.json()) as { syncs: NangoSyncStatus[] };
      return json.syncs;
    },
    enabled: !!tableId,
    refetchInterval: syncRefetchInterval,
  });
}

/**
 * Get sync statuses for a Nango connection (all syncs, regardless of whether
 * they have a corresponding table/connector in our DB).
 */
export function useConnectionSyncsQuery(
  connectionId: string | undefined,
  providerConfigKey: string | undefined
) {
  const rpc = useRpc();

  return useQuery({
    queryKey: queryKeys.connectors.connectionSyncs(connectionId ?? ''),
    queryFn: async (): Promise<NangoSyncStatus[]> => {
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.connectors.nango.connections[':connectionId'].syncs.$get({
        param: { connectionId: connectionId! },
        query: { providerConfigKey: providerConfigKey! },
      });
      if (!res.ok) await throwApiError(res, 'Failed to fetch connection syncs');
      const json = (await res.json()) as { syncs: NangoSyncStatus[] };
      return json.syncs;
    },
    enabled: !!connectionId && !!providerConfigKey,
    refetchInterval: syncRefetchInterval,
  });
}

/**
 * Pause a Nango sync for a table's connector.
 */
export function usePauseNangoSync() {
  const rpc = useRpc();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (tableId: string) => {
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.connectors.nango[':tableId']['pause-sync'].$post({
        param: { tableId },
      });
      if (!res.ok) await throwApiError(res, 'Failed to pause sync');
      return tableId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.connectors.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.tables.all });
    },
  });
}

/**
 * Resume a paused Nango sync for a table's connector.
 */
export function useResumeNangoSync() {
  const rpc = useRpc();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (tableId: string) => {
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.connectors.nango[':tableId']['resume-sync'].$post({
        param: { tableId },
      });
      if (!res.ok) await throwApiError(res, 'Failed to resume sync');
      return tableId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.connectors.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.tables.all });
    },
  });
}

// ============================================================================
// Connection Management
// ============================================================================

/**
 * Create tables from a Nango connection (auto-creates one table per sync).
 */
export function useCreateFromConnection() {
  const rpc = useRpc();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      connectionId,
      providerConfigKey,
      syncNames,
    }: {
      connectionId: string;
      providerConfigKey: string;
      syncNames?: string[];
    }) => {
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.connectors.nango['create-from-connection'].$post({
        json: { connectionId, providerConfigKey, syncNames },
      });
      if (!res.ok) await throwApiError(res, 'Failed to create integration');
      const json = (await res.json()) as {
        tables: { id: string; name: string }[];
        connectors: TableConnector[];
      };
      return json;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.connectors.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.tables.all });
    },
  });
}

/**
 * Disconnect a Nango integration (removes all connectors for a connection).
 */
export function useDisconnectIntegration() {
  const rpc = useRpc();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      connectionId,
      providerConfigKey,
    }: {
      connectionId: string;
      providerConfigKey: string;
    }) => {
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.connectors.nango.connections[':connectionId'].$delete({
        param: { connectionId },
        json: { providerConfigKey },
      });
      if (!res.ok) await throwApiError(res, 'Failed to disconnect integration');
      const json = (await res.json()) as {
        deletedConnectorIds: string[];
        affectedTableIds: string[];
      };
      return json;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.connectors.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.tables.all });
    },
  });
}

/**
 * Fetch workspace-level Nango connections (tagged by workspace).
 */
export function useWorkspaceConnectionsQuery() {
  const rpc = useRpc();

  return useQuery({
    queryKey: queryKeys.connectors.workspaceConnections,
    queryFn: async (): Promise<WorkspaceNangoConnection[]> => {
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.connectors.nango['workspace-connections'].$get();
      if (!res.ok) await throwApiError(res, 'Failed to fetch workspace connections');
      const json = (await res.json()) as { connections: WorkspaceNangoConnection[] };
      return json.connections;
    },
    staleTime: 30_000,
  });
}
